import React, { useState, useMemo, useEffect, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { ChevronRight, Search, ShieldCheck, Award, LayoutGrid, Users, Truck } from 'lucide-react';
import { useBrands } from '../hooks/useBrandsQuery';
import { useBrandsPageSettings } from '../hooks/useHomepageQueries';
import { LoadingSkeleton, ErrorFallback } from '../components/ui/SuspenseComponents';
import Pagination from '../components/ui/Pagination';
import { getImageUrl } from '../lib/utils';

const BRANDS_PER_PAGE = 20;

const TrustBadge = ({ icon, title, subtitle }) => (
  <div className="flex items-center gap-2.5">
    <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-red-50 text-red-600 flex items-center justify-center">
      {icon}
    </span>
    <div>
      <p className="text-xs font-bold text-gray-900 leading-tight">{title}</p>
      <p className="text-[11px] text-gray-500 leading-tight">{subtitle}</p>
    </div>
  </div>
);

const PromoItem = ({ icon, title, subtitle }) => (
  <div className="flex items-center gap-3">
    <span className="flex-shrink-0 w-10 h-10 rounded-lg bg-white text-red-600 flex items-center justify-center shadow-sm">
      {icon}
    </span>
    <div>
      <p className="text-xs md:text-sm font-bold text-gray-900 leading-tight">{title}</p>
      <p className="text-[11px] md:text-xs text-gray-500 leading-tight">{subtitle}</p>
    </div>
  </div>
);

// Main content component that uses the hook
const BrandsContent = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { data: brands } = useBrands();
  const { data: brandsPageSettings } = useBrandsPageSettings();
  const heroBannerImage = brandsPageSettings?.heroBannerImage || '/images/about-collage-transparent.png';
  const aboutBannerImage = brandsPageSettings?.aboutBannerImage || '/images/brand_banner_2.png';

  // State for the selected letter filter and search query
  const [selectedLetter, setSelectedLetter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Generate A-Z letters for the filter
  const alphabet = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i));

  // Function to check if any brands start with a given letter
  const hasBrands = letter => {
    return brands.some(brand =>
      brand.name.toUpperCase().startsWith(letter)
    );
  };

  // Handle alphabet filter selection
  const handleLetterFilter = (letter) => {
    if (selectedLetter === letter) {
      setSelectedLetter('all');
    } else {
      setSelectedLetter(letter);
      // Clear search when using letter filter
      setSearchQuery('');
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedLetter('all');
    setSearchQuery('');
  };

  // Filter brands based on selected letter and search query
  const filteredBrands = useMemo(() => {
    if (!brands.length) return [];

    return brands.filter(brand => {
      // Check if matches search query
      const matchesSearch = searchQuery
        ? brand.name.toLowerCase().includes(searchQuery.toLowerCase())
        : true;

      // Check if matches letter filter
      let matchesLetter = true;
      if (selectedLetter !== 'all') {
        if (selectedLetter === 'popular') {
          matchesLetter = brand.featured;
        } else {
          matchesLetter = brand.name.toUpperCase().startsWith(selectedLetter);
        }
      }

      return matchesSearch && matchesLetter;
    });
  }, [brands, selectedLetter, searchQuery]);

  // Reset to first page whenever the filtered set changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedLetter]);

  const totalPages = Math.ceil(filteredBrands.length / BRANDS_PER_PAGE);
  const paginatedBrands = filteredBrands.slice(
    (currentPage - 1) * BRANDS_PER_PAGE,
    currentPage * BRANDS_PER_PAGE
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="w-full bg-white">
      {/* Breadcrumb */}
      <div className="container mx-auto px-4 pt-4">
        <nav className="flex items-center gap-1.5 text-xs text-gray-500">
          <Link to="/" className="hover:text-gray-700 transition-colors">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-red-600 font-medium">Brands</span>
        </nav>
      </div>

      {/* Hero */}
      <div className="bg-gradient-to-br from-red-50 via-white to-white">
        <div className="container mx-auto px-4 py-8 md:py-12">
          <div className="grid md:grid-cols-2 gap-6 md:gap-8 items-center">
            <div className="order-2 md:order-1">
              <h1 className="text-2xl md:text-4xl font-semibold text-gray-900 leading-tight mb-3">
                Trusted Brands for<br />
                <span className="text-red-600">Surgical</span>, <span className="text-[#1B365D]">Dental</span> &amp; <span className="text-amber-500">Skincare</span>
              </h1>
              <p className="text-sm md:text-base text-gray-500 max-w-md mb-6 leading-relaxed">
                Explore a wide range of top international and local brands in surgical, dental, and skincare products handpicked for quality, safety, and reliability.
              </p>
              <div className="flex flex-wrap gap-x-6 gap-y-4">
                <TrustBadge icon={<ShieldCheck className="w-4 h-4" />} title="100% Authentic" subtitle="Genuine products only" />
                <TrustBadge icon={<Award className="w-4 h-4" />} title="Quality Assured" subtitle="Trusted by professionals" />
                <TrustBadge icon={<LayoutGrid className="w-4 h-4" />} title="Wide Selection" subtitle="Surgical, Dental & Skincare" />
              </div>
            </div>

            <div className="flex justify-center order-1 md:order-2">
              <div className="w-full max-w-[220px] md:max-w-sm">
                <img
                  src={heroBannerImage}
                  alt="Surgical, dental, and skincare products"
                  className="w-full h-auto object-contain mix-blend-multiply"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and A-Z Filter */}
      <div className="sticky top-[84px] md:top-[92px] bg-white shadow-sm border-y border-gray-100 z-40">
        <div className="container mx-auto px-4 py-4">
          {/* Search Bar */}
          <div className="relative mb-3">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                if (e.target.value) setSelectedLetter('all');
              }}
              placeholder="Search brands..."
              className="w-full py-2.5 pl-10 pr-4 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-1 focus:ring-red-400 focus:border-red-400 transition-colors"
            />
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 pointer-events-none">
              <Search className="w-4 h-4" />
            </div>
          </div>

          {/* A-Z Filter */}
          <div className="flex flex-wrap items-center gap-1 md:gap-1.5">
            {/* Popular filter option */}
            <button
              onClick={() => {
                handleLetterFilter('popular');
              }}
              className={`text-xs md:text-sm py-1 px-2.5 rounded-full font-medium transition-colors ${
                selectedLetter === 'popular'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Popular
            </button>

            {/* All filter option */}
            <button
              onClick={clearFilters}
              className={`text-xs md:text-sm py-1 px-2.5 rounded-full font-medium transition-colors ${
                selectedLetter === 'all' && !searchQuery
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>

            {/* A-Z filter options */}
            {alphabet.map(letter => {
              const hasBrandsWithLetter = hasBrands(letter);
              return (
                <button
                  key={letter}
                  onClick={() => hasBrandsWithLetter && handleLetterFilter(letter)}
                  disabled={!hasBrandsWithLetter}
                  className={`text-xs md:text-sm py-1 px-2 rounded-full font-medium transition-colors ${
                    selectedLetter === letter
                      ? 'bg-red-600 text-white'
                      : hasBrandsWithLetter
                        ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        : 'bg-gray-100 text-gray-400 opacity-40 cursor-not-allowed'
                  }`}
                >
                  {letter}
                </button>
              );
            })}
          </div>

          {/* Active Filters Display */}
          {(selectedLetter !== 'all' || searchQuery) && (
            <div className="flex items-center justify-between pt-3 text-sm">
              <div className="flex items-center flex-wrap gap-2">
                <span className="text-gray-500">Active filters:</span>
                {selectedLetter !== 'all' && (
                  <span className="bg-red-50 text-red-700 px-2 py-0.5 rounded-full">
                    {selectedLetter === 'popular' ? 'Popular brands' : `Starts with: ${selectedLetter}`}
                  </span>
                )}
                {searchQuery && (
                  <span className="bg-red-50 text-red-700 px-2 py-0.5 rounded-full">
                    "{searchQuery}"
                  </span>
                )}
              </div>
              <button
                onClick={clearFilters}
                className="text-red-600 hover:text-red-800 font-medium"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Brands Grid */}
      <div className="container mx-auto px-4 py-8">
        {filteredBrands.length > 0 ? (
          <>
            <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-4">
              {selectedLetter === 'all' ? 'All Brands' :
               selectedLetter === 'popular' ? 'Popular Brands' :
               `Brands: ${selectedLetter}`}
               <span className="text-sm font-normal text-gray-500 ml-2">
                ({filteredBrands.length})
               </span>
            </h2>

            {/* Brands Grid - Mobile optimized with 3 columns on mobile, more on larger screens */}
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3 sm:gap-4">
              {paginatedBrands.map(brand => (
                <Link
                  key={brand._id}
                  to={`/brands/${brand.slug}`}
                  className="bg-white rounded-lg border border-gray-100 shadow-sm p-3 flex flex-col items-center text-center hover:shadow-md hover:border-red-100 hover:-translate-y-0.5 transition-all duration-300"
                >
                  <div className="h-10 md:h-12 flex items-center justify-center mb-2">
                    {brand.picture ? (
                      <img
                        src={getImageUrl(brand.picture)}
                        alt={`${brand.name} logo`}
                        className="max-h-full object-contain"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-gray-700 text-lg font-semibold">
                        {brand.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <h3 className="text-xs md:text-sm font-semibold text-gray-800 leading-tight">
                    {brand.name}
                  </h3>
                  {brand.tags?.[0] && (
                    <p className="text-[10px] text-gray-400 mt-0.5">{brand.tags[0]}</p>
                  )}
                  {brand.featured && (
                    <span className="mt-2 text-[10px] font-medium bg-amber-50 text-amber-700 py-0.5 px-2 rounded-full">
                      Popular
                    </span>
                  )}
                </Link>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-8">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <div className="mx-auto h-16 w-16 mb-4 text-gray-400 flex items-center justify-center">
              <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Brands Found</h3>
            <p className="text-gray-500 mb-6">
              {searchQuery
                ? "We couldn't find any brands matching your search."
                : selectedLetter === 'popular'
                  ? "There are no popular brands at the moment."
                  : `We couldn't find any brands starting with '${selectedLetter}'.`}
            </p>
            <button
              onClick={clearFilters}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
            >
              View All Brands
            </button>
          </div>
        )}
      </div>

      {/* Promo Strip */}
      <div className="bg-red-50/60 border-y border-red-100">
        <div className="container mx-auto px-4 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            <PromoItem icon={<LayoutGrid className="w-4 h-4" />} title="Surgical, Dental & Skincare" subtitle="All your needs in one place" />
            <PromoItem icon={<Users className="w-4 h-4" />} title="Trusted by Professionals" subtitle="Used by clinics and hospitals" />
            <PromoItem icon={<ShieldCheck className="w-4 h-4" />} title="Quality You Can Trust" subtitle="International & local top brands" />
            <PromoItem icon={<Truck className="w-4 h-4" />} title="Fast & Reliable Delivery" subtitle="Across Nepal" />
          </div>
        </div>
      </div>

      {/* About Our Brands */}
      <div className="container mx-auto px-4 py-10 md:py-14">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div className="flex justify-center order-2 md:order-1">
            <img
              src={aboutBannerImage}
              alt="CureHive Nepal healthcare and wellness products"
              className="w-full max-w-sm h-auto object-contain"
            />
          </div>
          <div className="order-1 md:order-2">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">About Our Brands</h2>
            <div className="text-sm md:text-base text-gray-600 space-y-3 leading-relaxed">
              <p>
                At CureHive Nepal, we collaborate with leading global and local brands in surgical, dental,
                and skincare industries to bring you products that meet the highest standards of safety,
                performance, and innovation.
              </p>
              <p>
                Whether you are a healthcare professional, clinic, or individual, our carefully curated brand
                selection ensures you get the best quality products for better care and results.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main component with Suspense and Error Boundary
const BrandsPage = () => {
  return (
    <ErrorBoundary
      FallbackComponent={({ error, resetErrorBoundary }) => (
        <div className="w-full bg-white">
          <div className="container mx-auto px-4 py-8">
            <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900">Trusted Brands</h1>
            <p className="text-sm md:text-base text-gray-500 mt-2">
              Browse quality healthcare products from trusted medical brands
            </p>
          </div>
          <div className="container mx-auto px-4 py-8">
            <ErrorFallback error={error} resetErrorBoundary={resetErrorBoundary} type="brands" />
          </div>
        </div>
      )}
      onReset={() => window.location.reload()}
    >
      <Suspense fallback={<LoadingSkeleton type="brands" />}>
        <BrandsContent />
      </Suspense>
    </ErrorBoundary>
  );
};

export default BrandsPage;
